import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import { ecosystemRequest } from '../../services/api/passportEcosystem';
import { StudioLookup, StudioTimezone } from './StudioFields';
import { PlaceLocationEditor } from '../places/PlaceLocationEditor';
import { defaultCardDesign, type PassportCard as Card } from '../../services/api/passportCards';
import SubjectPassportQrPanel from './SubjectPassportQrPanel';
const StudioPreview = React.lazy(() => import('./StudioPreview'));
type Place = Record<string, unknown>;
const blank: Place = {
  category: 'cafe',
  timezone: 'America/Denver',
  address_policy: 'coarse',
  status: 'draft',
  collection_enabled: true,
};
export default function PassportPlaceManager({ internal = false }: { internal?: boolean }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<Place[]>([]),
    [form, setForm] = useState<Place>(blank),
    [message, setMessage] = useState(''),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  const [back, setBack] = useState(false);
  const [baseTemplate, setBaseTemplate] = useState<{
    id: number;
    visual: Card['design']['visual'];
  } | null>(null);
  useEffect(() => {
    if (!internal) return;
    let active = true;
    void ecosystemRequest<
      Array<{ kind: string; public_id: string; published_version_id: number | null }>
    >('templates', undefined, {}, 'admin')
      .then(async (templates) => {
        const newest = templates
          .filter((t) => t.kind === 'place' && t.published_version_id)
          .sort((a, b) => b.published_version_id! - a.published_version_id!)[0];
        if (!newest) return;
        const versions = await ecosystemRequest<Array<{ id: number; visual_json: string }>>(
          'template_versions',
          undefined,
          { id: newest.public_id },
          'admin'
        );
        const version = versions.find((v) => v.id === newest.published_version_id);
        if (active && version)
          setBaseTemplate({ id: version.id, visual: JSON.parse(version.visual_json) });
      })
      .catch(() => {
        if (active)
          setMessage(
            'The current place template could not load. Reload before relying on the preview.'
          );
      });
    return () => {
      active = false;
    };
  }, [internal]);
  const placePreview: Card = {
    id: null,
    subject_type: 'place',
    subject_id: Number(form.id || 0),
    title: String(form.name || 'Your place'),
    issuer: 'Bilt:Spec',
    status: 'draft',
    template_version_id: baseTemplate?.id,
    design: { ...defaultCardDesign, template: 'place', visual: baseTemplate?.visual },
    rules: { evidence: 'qr', cap: null },
    subject: {
      subject_type: 'place',
      subject_id: Number(form.id || 0),
      title: String(form.name || 'Your place'),
      image_url: form.image_path ? String(form.image_path) : null,
      facts: {
        category: String(form.category),
        city: String(form.city || ''),
        operator: String(form.operator_label || ''),
        offering: String(form.offering || ''),
        exhibition: String(form.exhibition || ''),
        access: String(form.access || ''),
        hours: String(form.hours || ''),
      },
    },
  };
  const area = internal ? 'admin' : 'collector';
  const select = (p: Place) => setForm({ ...p, ...JSON.parse(String(p.facts_json || '{}')) });
  useEffect(() => {
    let active = true;
    setRows([]);
    setForm(blank);
    void ecosystemRequest<Place[]>(internal ? 'places' : 'own_places', undefined, {}, area)
      .then((r) => {
        if (active) {
          setRows(r);
          if (!internal && r[0]) select(r[0]);
        }
      })
      .catch((e) => {
        if (active) setMessage(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [internal, area, user?.id]);
  const refresh = (r: Place) => {
    select(r);
    setRows((current) => [r, ...current.filter((p) => p.id !== r.id)]);
  };
  const save = async () => {
    setBusy(true);
    try {
      refresh(await ecosystemRequest<Place>('place_save', form, {}, area));
      setMessage('Place saved.');
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const photo = async (file?: File) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append('place_id', String(form.id));
      body.append('revision', String(form.revision));
      if (file) body.append('image', file);
      else body.append('remove', '1');
      const result = await api.makeRequest<{ revision: number; image_url: string | null }>(
        '/passport_place_media.php',
        { method: 'POST', body }
      );
      if (!result.success || !result.data)
        throw new Error(result.error || 'Photo could not be saved');
      refresh({ ...form, revision: result.data.revision, image_path: result.data.image_url });
      setMessage(file ? 'Place photograph saved.' : 'Place photograph removed.');
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="pc-page">
      <Link to="/passport/operations">Program operations</Link>
      <h1>{internal ? 'Persistent places' : 'Your places'}</h1>
      <p>Edit the real place information used by its public profile and automatic collectible.</p>
      {loading ? (
        <p role="status">Loading managed places…</p>
      ) : !internal && !rows.length ? (
        <p>No places are assigned to this account.</p>
      ) : (
        <div className={internal ? 'ps-workspace' : 'pe-admin-split'}>
          <div className="ps-controls">
            <div className="ps-library">
              {rows.map((r) => (
                <button
                  key={String(r.id)}
                  className="pc-button"
                  disabled={busy}
                  onClick={() => select(r)}
                >
                  {String(r.name)} · {String(r.status)}
                </button>
              ))}
              {internal && (
                <button className="pc-button" onClick={() => setForm(blank)}>
                  New place
                </button>
              )}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <fieldset disabled={busy}>
                <legend>Describe the real place</legend>
                <label>
                  Place name
                  <input
                    required
                    value={String(form.name || '')}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  <span className="ps-help">
                    Use the name visitors recognize on the venue’s website and signage.
                  </span>
                </label>
                {internal && (
                  <StudioLookup
                    label="Place operator"
                    kind="account"
                    value={String(form.operator_id || '')}
                    onChange={(v) => setForm({ ...form, operator_id: v })}
                    help="Choose the Bilt account responsible for maintaining this place. This grants place management, not Studio access."
                  />
                )}
                <label>
                  Public operator name
                  <input
                    value={String(form.operator_label || '')}
                    onChange={(e) => setForm({ ...form, operator_label: e.target.value })}
                  />
                  <span className="ps-help">
                    The business or organization shown to visitors, for example the museum’s
                    operator.
                  </span>
                </label>
                <h3>Find the location</h3>
                <PlaceLocationEditor
                  idPrefix="passport-place"
                  value={{
                    place_id: String(form.place_id || ''),
                    location_label: String(form.name || ''),
                    formatted_address: String(form.address || ''),
                    latitude: form.latitude ? Number(form.latitude) : null,
                    longitude: form.longitude ? Number(form.longitude) : null,
                    city: String(form.city || ''),
                    state: String(form.state || ''),
                  }}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      place_id: v.place_id,
                      address: v.formatted_address,
                      latitude: v.latitude,
                      longitude: v.longitude,
                      city: v.city,
                      state: v.state,
                    })
                  }
                />
                <label>
                  Street address
                  <input
                    value={String(form.address || '')}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                  <span className="ps-help">
                    Confirm the visitor entrance with the venue. Address visibility is controlled
                    below.
                  </span>
                </label>
                <StudioTimezone
                  value={String(form.timezone)}
                  onChange={(v) => setForm({ ...form, timezone: v })}
                />
                <details>
                  <summary>Adjust the location coordinates manually</summary>
                  <p className="ps-help">
                    Optional private coordinates, in decimal degrees. Copy the venue entrance’s pin
                    from a map when search is unavailable. These do not make a private address
                    public.
                  </p>
                  {['latitude', 'longitude'].map((k) => (
                    <label key={k}>
                      {k === 'latitude' ? 'Latitude' : 'Longitude'}
                      <input
                        type="number"
                        step="any"
                        min={k === 'latitude' ? -90 : -180}
                        max={k === 'latitude' ? 90 : 180}
                        value={String(form[k] ?? '')}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </label>
                  ))}
                </details>
                {[
                  [
                    'offering',
                    'What visitors can do here',
                    'Describe the actual offering, such as coffee, exhibitions or workshops.',
                  ],
                  [
                    'exhibition',
                    'Exhibition or collection',
                    'For museums: name the current exhibition or permanent collection. Leave blank if not applicable.',
                  ],
                  [
                    'access',
                    'Before a visitor arrives',
                    'Confirm accessibility, reservations and entry requirements with the venue. A Passport card is not admission.',
                  ],
                  [
                    'hours',
                    'Opening hours',
                    'Copy the venue’s current public hours, including closure days. Do not guess.',
                  ],
                ].map(([k, l, help]) => (
                  <label key={k}>
                    {l}
                    <span className="ps-help">{help}</span>
                    <textarea
                      value={String(form[k] ?? '')}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      maxLength={1000}
                    />
                  </label>
                ))}
                {(
                  [
                    ['category', ['cafe', 'museum', 'venue', 'scenic_destination', 'other']],
                    ['address_policy', ['coarse', 'public']],
                    [
                      'status',
                      internal
                        ? ['draft', 'public', 'private', 'closed']
                        : form.status === 'draft'
                          ? ['draft']
                          : ['public', 'private', 'closed'],
                    ],
                  ] as Array<[string, string[]]>
                ).map(([k, options]) => (
                  <label key={k}>
                    {
                      {
                        category: 'Kind of place',
                        address_policy: 'What location visitors can see',
                        status: 'Visibility and availability',
                      }[k]
                    }
                    <select
                      value={String(form[k])}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    >
                      {options.map((v) => (
                        <option key={v} value={v}>
                          {
                            {
                              cafe: 'Café',
                              museum: 'Museum',
                              venue: 'Venue',
                              scenic_destination: 'Scenic destination',
                              other: 'Other place',
                              coarse: 'Show city / area only',
                              public:
                                k === 'status'
                                  ? 'Public and discoverable'
                                  : 'Show full street address',
                              draft: 'Draft: not yet public',
                              private: 'Private: hidden from visitors',
                              closed: 'Closed: no new visits',
                            }[v]
                          }
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(Number(form.collection_enabled))}
                    onChange={(e) => setForm({ ...form, collection_enabled: e.target.checked })}
                  />
                  Let visitors collect this place’s automatic card
                </label>
                <p className="ps-help">
                  Only a public, collection-enabled place can be collected. Saving a draft lets you
                  review its information first. Closing or hiding a place preserves existing
                  collector history.
                </p>
                <button className="pc-button">Save place</button>
              </fieldset>
            </form>
            {Boolean(form.id) && (
              <>
                <label>
                  Place photograph
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={busy}
                    onChange={(e) => {
                      if (e.target.files?.[0]) void photo(e.target.files[0]);
                    }}
                  />
                </label>
                {Boolean(form.image_path) && (
                  <button className="pc-link" disabled={busy} onClick={() => void photo()}>
                    Remove photograph
                  </button>
                )}
                <Link className="pc-link" to={`/places/${form.slug}`}>
                  View public place
                </Link>
                <SubjectPassportQrPanel
                  subjectType="place"
                  subjectId={Number(form.id)}
                  subjectLabel={String(form.name)}
                />
              </>
            )}
          </div>
          {internal && (
            <React.Suspense fallback={<p role="status">Loading card preview…</p>}><StudioPreview card={placePreview} back={back} onBack={setBack}>
              <p className="ps-help">
                Preview of your current place facts with the published place template, or the
                standard layout if no default has been published. This is not a saved collectible.
              </p>
            </StudioPreview></React.Suspense>
          )}
        </div>
      )}
      <p role="status">{message}</p>
    </section>
  );
}
